import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { Content, Paragraph } from '@/api/types/stowed-element'
import { normalizeDropCapValue } from '@/lib/text/drop-cap'
import { bakeShapeTextWireLines } from '@/lib/shapes/bake-shape-text-wire-lines'
import { hasShapeTextContent } from '@/lib/shapes/shape-text-frame'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { ShapeTextBakeContext } from '../types/shape-text-bake-context'
import { buildWireParagraphs } from '@/lib/elements/wire-paragraphs'

const hasActiveDropCap = (paragraph: Paragraph): boolean =>
  normalizeDropCapValue(paragraph.dropCap) !== 'NONE'

/**
 * `paragraphs != null` — not `hasShapeTextContent` — is the write-side gate, matching
 * `isShapeTextFrame`'s "presence, not content, is the test" (WMPCM-8160): an empty converted
 * frame (`paragraphs: []`, no text typed yet) must still write `content.text: { paragraphs: [] }`
 * so it survives a real reload as a text frame rather than reverting to a plain shape.
 */
export function buildShapeTextContent(
  shape: ShapeData,
  fonts?: ParagraphBlockFontLookup,
  bake?: ShapeTextBakeContext
): Content | undefined {
  const { paragraphs } = shape

  if (paragraphs == null) return undefined

  if (!hasShapeTextContent(paragraphs)) {
    // An empty frame normally writes no paragraphs, but a paragraph whose Drop Cap is switched on
    // carries settings that belong to the frame (they apply to the first character once text is
    // typed), so those paragraphs are written as-is rather than dropped. There is no text to bake.
    return {
      text: {
        paragraphs: paragraphs.some(hasActiveDropCap)
          ? buildWireParagraphs(paragraphs, fonts, true)
          : [],
      },
    }
  }

  // yb360 paints shape text from the baked `<TL>`/`<TR>` geometry on load, so the wire must carry
  // the *visual* scanline rows (per-row `LM`, absolute `BL`) rather than larry's one-line-per-
  // paragraph logical model — otherwise the whole paragraph is drawn as a single line from the
  // bounding box's left edge, outside the outline. Falls back to the logical form only when no
  // layout is possible (shape too small for one row), which keeps the text on the wire either way.
  return {
    text: {
      paragraphs:
        bakeShapeTextWireLines(paragraphs, shape, fonts, bake?.wrap, bake?.hasNextInChain) ??
        buildWireParagraphs(paragraphs, fonts, true),
    },
  }
}
